import { LOCAL_STORAGE_KEY_JWT } from '@/api'
import { NECTAR_API_URL } from '@/env'
import { routes } from '@/pages'
import axios, { AxiosRequestConfig } from 'axios'
import { clearProlificSession, getProlificToken } from '../prolific/session'

/** Per-request opt-out: when true, a 401 won't hard-redirect to /login (the caller handles it). */
type NectarRequestConfig = AxiosRequestConfig & { skipAuthRedirect?: boolean }

const nectarClient = axios.create({ baseURL: NECTAR_API_URL })

nectarClient.interceptors.request.use((config) => {
  try {
    const prolificToken = getProlificToken()
    if (prolificToken) {
      config.headers.Authorization = `Bearer ${prolificToken}`
      if (config.url?.startsWith('/v1/projects')) {
        config.url = config.url.replace('/v1/projects', '/v1/prolific/projects')
      }
      return config
    }
    if (config.headers?.Authorization !== undefined) {
      return config
    }
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY_JWT)
    if (raw) {
      const token = JSON.parse(raw)
      config.headers.Authorization = `Bearer ${token}`
    }
  } catch (error) {
    console.error('Error reading token', error)
  }

  return config
})

nectarClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const skipAuthRedirect = (error.config as NectarRequestConfig | undefined)?.skipAuthRedirect
    if (error.response?.status === 401 && !skipAuthRedirect) {
      if (getProlificToken()) {
        clearProlificSession()
        window.location.replace('/prolific?expired=1')
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEY_JWT)
        window.location.replace(routes.login.path)
      }
    }
    if (
      error.response?.status === 403 &&
      error.response?.data?.error === 'You are not allowed to access this resource'
    ) {
      if (getProlificToken()) { clearProlificSession() }
      localStorage.removeItem(LOCAL_STORAGE_KEY_JWT)
      window.location.replace(routes.ban.path)
    }
    return Promise.reject(error)
  }
)

export const nectarApi = {
  get: <T>(url: string, params?: Record<string, unknown> | URLSearchParams, config?: NectarRequestConfig): Promise<T> =>
    nectarClient.get(url, { params, ...config }).then(({ data }) => data),
  post: <T>(url: string, data?: unknown, config?: NectarRequestConfig): Promise<T> =>
    nectarClient.post(url, data, config).then(({ data: res }) => res)
}
